// Start page: three big actions (meeting, inspection, reminder) and recent documents with search.
import { useEffect, useState } from 'react';
import type { DocType, DocumentSummary } from '../../../shared/types';
import { isAndroidApp } from '../lib/android';
import { createDocument } from '../lib/api';
import { DOC_TYPE_LABEL, defaultTitle, formatDateTime, formatDue, formatDuration, plural } from '../lib/format';
import { useDocuments } from '../lib/hooks';
import { noteLabel } from '../lib/notes';
import { navigate } from '../lib/router';
import { errorText, newId, routeForDocument, truncate, useDebouncedValue, useNow } from '../components/helpers';
import { Icon, type IconName } from '../components/Icon';
import { Link } from '../components/NavGuard';
import { ProjectChip, ProjectFilter } from '../components/ProjectPicker';
import { Chip, SummaryChips } from '../components/StatusChip';
import { toast } from '../components/Toasts';
import { requestNoteAutoStart } from './Notes';

const TYPE_ICON: Record<DocType, IconName> = { meeting: 'meeting', inspection: 'inspection', note: 'note' };
const SEARCH_DEBOUNCE_MS = 300;
/** Documents shown at first and added by every "Pokaži več" (the server returns at most 200). */
const RECENT_LIMIT = 30;
const RECENT_MAX = 200;

function metaLine(s: DocumentSummary): string {
  const parts = [DOC_TYPE_LABEL[s.type], formatDateTime(s.createdAt)];
  if (s.type === 'inspection' && s.clipCount > 0) parts.push(plural(s.clipCount, ['vnos', 'vnosa', 'vnosi', 'vnosov']));
  else if (s.durationMs > 0) parts.push(formatDuration(s.durationMs));
  return parts.join(' · ');
}

function DocumentItem({ summary, now }: { summary: DocumentSummary; now: Date }) {
  const title = summary.type === 'note' ? noteLabel(summary) : summary.title;
  const overdue = summary.type === 'note' && !summary.done && summary.dueAt !== null && new Date(summary.dueAt) < now;
  return (
    <li>
      <Link to={routeForDocument(summary)} className="doc-item">
        <span className={`doc-item__icon doc-item__icon--${summary.type}`}>
          <Icon name={TYPE_ICON[summary.type]} />
        </span>
        <span className="doc-item__main">
          <span className={`doc-item__title${summary.done ? ' is-done' : ''}`}>{truncate(title, 120)}</span>
          <span className="doc-item__meta">{metaLine(summary)}</span>
          {summary.type !== 'note' && summary.preview && <span className="doc-item__preview">{truncate(summary.preview, 160)}</span>}
          <span className="doc-item__chips">
            <ProjectChip doc={summary} />
            <SummaryChips summary={summary} />
            {summary.type === 'note' && summary.done && <Chip tone="done">Opravljeno</Chip>}
            {summary.type === 'note' && !summary.done && summary.dueAt && (
              <Chip tone={overdue ? 'overdue' : 'due'}>
                <Icon name="clock" size={14} /> {formatDue(summary.dueAt, now)}
              </Chip>
            )}
          </span>
        </span>
        <Icon name="chevronRight" className="doc-item__chev" />
      </Link>
    </li>
  );
}

export function HomePage() {
  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const [limit, setLimit] = useState(RECENT_LIMIT);
  // '' = every project, 'none' = documents without one (filtered here), else a project id (the server filters).
  const [projectFilter, setProjectFilter] = useState('');
  // A new search or filter starts with a short list again.
  useEffect(() => setLimit(RECENT_LIMIT), [q, projectFilter]);
  const { data: fresh, loading, error, stale, reload } = useDocuments({
    q: q || undefined,
    limit,
    project: projectFilter && projectFilter !== 'none' ? projectFilter : undefined,
  });
  // Keep the previous results on screen while a new search loads (no flicker while typing).
  const [previous, setPrevious] = useState<DocumentSummary[] | null>(null);
  useEffect(() => {
    if (fresh) setPrevious(fresh);
  }, [fresh]);
  const loaded = fresh ?? previous;
  const filtered = projectFilter === 'none' && loaded ? loaded.filter((s) => !s.projectId) : loaded;
  // Done reminders last (also when a reminder was just ticked off here), so open ones are never hidden among them.
  const data = filtered ? [...filtered.filter((s) => !(s.type === 'note' && s.done)), ...filtered.filter((s) => s.type === 'note' && s.done)] : filtered;
  const [creating, setCreating] = useState<DocType | null>(null);
  const now = useNow(60_000);
  const [inApp] = useState(isAndroidApp);

  const start = async (type: 'meeting' | 'inspection') => {
    if (creating) return;
    setCreating(type);
    const id = newId();
    try {
      await createDocument(id, { type, title: defaultTitle(type) }, { isNew: true });
      navigate(routeForDocument({ id, type }));
    } catch (err) {
      toast(errorText(err, 'Dokumenta ni bilo mogoče ustvariti.'), { kind: 'error' });
    } finally {
      setCreating(null);
    }
  };

  const startNote = () => {
    // The notes page starts recording right away: one tap from here to recording.
    requestNoteAutoStart();
    navigate({ name: 'notes' });
  };

  return (
    <div className="page page--home">
      <h1 className="sr-only">BAAM Transkripcija</h1>
      <div className="actions-grid">
        <button type="button" className="action-card action-card--meeting" onClick={() => void start('meeting')} disabled={creating !== null}>
          <span className="action-card__icon">
            <Icon name={creating === 'meeting' ? 'spinner' : 'meeting'} size={30} />
          </span>
          <span className="action-card__text">
            <span className="action-card__title">Sestanek</span>
            <span className="action-card__desc">Posname celoten pogovor in zapiše, kdo kaj govori.</span>
          </span>
        </button>
        <button type="button" className="action-card action-card--inspection" onClick={() => void start('inspection')} disabled={creating !== null}>
          <span className="action-card__icon">
            <Icon name={creating === 'inspection' ? 'spinner' : 'inspection'} size={30} />
          </span>
          <span className="action-card__text">
            <span className="action-card__title">Nadzor</span>
            <span className="action-card__desc">Zapisnik nadzora z glasovnimi vnosi.</span>
          </span>
        </button>
        <button type="button" className="action-card action-card--note" onClick={startNote}>
          <span className="action-card__icon">
            <Icon name="note" size={30} />
          </span>
          <span className="action-card__text">
            <span className="action-card__title">Opomnik</span>
            <span className="action-card__desc">Hiter glasovni zapisek, da česa ne pozabite.</span>
          </span>
        </button>
      </div>

      {inApp && (
        <p className="hint home-hint">
          <Icon name="phone" size={18} />
          <span>
            Snemanje z zaklenjenim zaslonom deluje s pripomočki aplikacije na začetnem zaslonu telefona (Opomnik, Nadzor, Sestanek) – snemanje v tem
            oknu se ob zaklepu ustavi.
          </span>
        </p>
      )}

      <section className="recent" aria-labelledby="recent-title">
        <div className="section-head">
          <h2 id="recent-title" className="section-title">
            Nedavni dokumenti
          </h2>
          <Link to={{ name: 'notes' }} className="btn btn--ghost btn--small">
            Vsi opomniki
          </Link>
        </div>
        <div className="search">
          <Icon name="search" size={20} className="search__icon" />
          <input
            type="search"
            className="search__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Iskanje po naslovih in besedilu …"
            aria-label="Iskanje po dokumentih"
            enterKeyHint="search"
          />
          {query && (
            <button type="button" className="icon-btn search__clear" aria-label="Počisti iskanje" onClick={() => setQuery('')}>
              <Icon name="close" size={20} />
            </button>
          )}
        </div>
        <ProjectFilter id="home-project-filter" value={projectFilter} onChange={setProjectFilter} />

        {stale && <p className="muted small offline-hint">Brez povezave – prikazani so podatki, shranjeni v tej napravi.</p>}

        {error && !data ? (
          <div className="notice notice--error" role="alert">
            <Icon name="alert" size={20} className="notice__icon" />
            <div className="notice__content">
              <p>{error}</p>
              <button type="button" className="btn btn--secondary btn--small" onClick={reload}>
                <Icon name="refresh" size={18} /> Poskusi znova
              </button>
            </div>
          </div>
        ) : !data || (!fresh && data.length === 0) ? (
          <p className="muted loading-line">
            <Icon name="spinner" size={18} /> Nalagam …
          </p>
        ) : data.length === 0 ? (
          <p className="empty">
            {q ? `Ni zadetkov za »${q}«.` : projectFilter ? 'V tem projektu še ni dokumentov.' : 'Še ni dokumentov. Začnite s sestankom, nadzorom ali opomnikom.'}
          </p>
        ) : (
          <>
            <ul className={`doc-list${loading ? ' is-loading' : ''}`} aria-busy={loading}>
              {data.map((s) => (
                <DocumentItem key={s.id} summary={s} now={now} />
              ))}
            </ul>
            {/* As many as asked for: older documents exist. */}
            {fresh !== null && fresh.length >= limit &&
              (limit < RECENT_MAX ? (
                <button
                  type="button"
                  className="btn btn--secondary load-more"
                  onClick={() => setLimit((l) => Math.min(RECENT_MAX, l + RECENT_LIMIT))}
                  disabled={loading}
                >
                  <Icon name={loading ? 'spinner' : 'chevronDown'} size={20} /> Pokaži več
                </button>
              ) : (
                <p className="muted small">Prikazanih je {RECENT_MAX} dokumentov. Starejše poiščite z iskanjem.</p>
              ))}
          </>
        )}
      </section>
    </div>
  );
}
